import {create} from 'zustand';
import {CreateMarketerRequest, UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest} from '../models/CreateMarketer';

interface MarketersUIState {
  // Tab Management (deprecated, kept for backward compatibility)
  activeTab: 'list' | 'add';
  setActiveTab: (tab: 'list' | 'add') => void;

  // New Marketer State
  hasNewMarketer: boolean;
  setHasNewMarketer: (hasNew: boolean) => void;

  // Search State
  search: string;
  setSearch: (search: string) => void;

  // Deleting State
  deletingMarketerId: number | null;
  setDeletingMarketerId: (id: number | null) => void;

  // Add Marketer Modal State
  isAddModalOpen: boolean;
  openAddModal: () => void;
  closeAddModal: () => void;

  // Add Marketer Form State
  addFormData: CreateMarketerRequest;
  addFormErrors: Record<string, string>;
  addFormShowPassword: boolean;

  // Add Form Actions
  setAddFormData: (data: Partial<CreateMarketerRequest>) => void;
  setAddFormErrors: (errors: Record<string, string>) => void;
  setAddFormShowPassword: (show: boolean) => void;
  resetAddForm: () => void;

  // Edit Marketer Form State
  editFormData: UpdateMarketerProfileRequest;
  editFormErrors: Record<string, string>;
  isEditModalOpen: boolean;
  editingMarketer: any | null;

  // Edit Form Actions
  setEditFormData: (data: Partial<UpdateMarketerProfileRequest>) => void;
  setEditFormErrors: (errors: Record<string, string>) => void;
  openEditModal: (marketer: any) => void;
  closeEditModal: () => void;
  resetEditForm: () => void;

  // Change Password Form State
  changePasswordData: UpdateMarketerPasswordRequest;
  changePasswordErrors: Record<string, string>;
  changePasswordShowPassword: boolean;
  isChangePasswordModalOpen: boolean;
  changingPasswordMarketer: any | null;

  // Change Password Actions
  setChangePasswordData: (data: Partial<UpdateMarketerPasswordRequest>) => void;
  setChangePasswordErrors: (errors: Record<string, string>) => void;
  setChangePasswordShowPassword: (show: boolean) => void;
  openChangePasswordModal: (marketer: any) => void;
  closeChangePasswordModal: () => void;
  resetChangePasswordForm: () => void;

  // Utility Actions
  reset: () => void;
}

const initialAddFormData: CreateMarketerRequest = {
  username: '',
  full_name: '',
  email: '',
  phone_number: '',
  password: '',
  role_id: 2 // Marketer role
};

const initialEditFormData: UpdateMarketerProfileRequest = {
  fullName: '',
  phoneNumber: '',
  email: ''
};

const initialChangePasswordData: UpdateMarketerPasswordRequest = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

export const useMarketersUIStore = create<MarketersUIState>((set) => ({
  // Tab Management (deprecated)
  activeTab: 'list',
  setActiveTab: (tab) => set({activeTab: tab}),

  // New Marketer State
  hasNewMarketer: false,
  setHasNewMarketer: (hasNew) => set({hasNewMarketer: hasNew}),

  // Search State
  search: '',
  setSearch: (search) => set({search}),

  // Deleting State
  deletingMarketerId: null,
  setDeletingMarketerId: (id) => set({deletingMarketerId: id}),

  // Add Marketer Modal State
  isAddModalOpen: false,
  openAddModal: () => set({isAddModalOpen: true}),
  closeAddModal: () => set({
    isAddModalOpen: false,
    addFormData: initialAddFormData,
    addFormErrors: {},
    addFormShowPassword: false
  }),

  // Add Marketer Form State
  addFormData: initialAddFormData,
  addFormErrors: {},
  addFormShowPassword: false,

  // Add Form Actions
  setAddFormData: (data) => set((state) => ({
    addFormData: {...state.addFormData, ...data}
  })),
  setAddFormErrors: (errors) => set({addFormErrors: errors}),
  setAddFormShowPassword: (show) => set({addFormShowPassword: show}),
  resetAddForm: () => set({
    addFormData: initialAddFormData,
    addFormErrors: {},
    addFormShowPassword: false
  }),

  // Edit Marketer Form State
  editFormData: initialEditFormData,
  editFormErrors: {},
  isEditModalOpen: false,
  editingMarketer: null,

  // Edit Form Actions
  setEditFormData: (data) => set((state) => ({
    editFormData: {...state.editFormData, ...data}
  })),
  setEditFormErrors: (errors) => set({editFormErrors: errors}),
  openEditModal: (marketer) => set({
    isEditModalOpen: true,
    editingMarketer: marketer,
    editFormData: {
      fullName: marketer.fullName || marketer.name || '',
      phoneNumber: marketer.phoneNumber || marketer.phone || '',
      email: marketer.email || ''
    }
  }),
  closeEditModal: () => set({
    isEditModalOpen: false,
    editingMarketer: null,
    editFormErrors: {}
  }),
  resetEditForm: () => set({
    editFormData: initialEditFormData,
    editFormErrors: {}
  }),

  // Change Password Form State
  changePasswordData: initialChangePasswordData,
  changePasswordErrors: {},
  changePasswordShowPassword: false,
  isChangePasswordModalOpen: false,
  changingPasswordMarketer: null,

  // Change Password Actions
  setChangePasswordData: (data) => set((state) => ({
    changePasswordData: {...state.changePasswordData, ...data}
  })),
  setChangePasswordErrors: (errors) => set({changePasswordErrors: errors}),
  setChangePasswordShowPassword: (show) => set({changePasswordShowPassword: show}),
  openChangePasswordModal: (marketer) => set({
    isChangePasswordModalOpen: true,
    changingPasswordMarketer: marketer,
    changePasswordData: initialChangePasswordData
  }),
  closeChangePasswordModal: () => set({
    isChangePasswordModalOpen: false,
    changingPasswordMarketer: null,
    changePasswordErrors: {}
  }),
  resetChangePasswordForm: () => set({
    changePasswordData: initialChangePasswordData,
    changePasswordErrors: {},
    changePasswordShowPassword: false
  }),

  // Utility Actions
  reset: () => set({
    activeTab: 'list',
    hasNewMarketer: false,
    search: '',
    deletingMarketerId: null,
    addFormData: initialAddFormData,
    addFormErrors: {},
    addFormShowPassword: false,
    editFormData: initialEditFormData,
    editFormErrors: {},
    isEditModalOpen: false,
    editingMarketer: null,
    changePasswordData: initialChangePasswordData,
    changePasswordErrors: {},
    changePasswordShowPassword: false,
    isChangePasswordModalOpen: false,
    changingPasswordMarketer: null
  })
}));
